import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './components/providers/theme-provider';
import { ToastProvider } from './components/ui/toast';
import { ConfirmProvider } from './components/ui/confirm-dialog';
import { PromptProvider } from './components/ui/prompt-dialog';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { MainLayout } from './components/layout/MainLayout';
import { RequireAuth } from './features/auth/components/RequireAuth';
import DashboardPage from './pages/DashboardPage';
import { PerfilPage } from './pages/PerfilPage';
import { CommunicationMatrixPage } from './pages/CommunicationMatrixPage';
import { RolesPage } from './pages/RolesPage';
import { UsersPage } from './pages/UsersPage';
import { SurveysPage } from './pages/SurveysPage';
import { SurveyFormsPage } from './pages/SurveyFormsPage';
import { SurveyResponsesPage } from './pages/SurveyResponsesPage';
import { SurveyStatisticsPage } from './pages/SurveyStatisticsPage';
import { SurveyTeableSheetPage } from './pages/SurveyTeableSheetPage';
import { PublicSurveyPage } from './pages/PublicSurveyPage';
import { AuthInitializer } from './components/AuthInitializer';
import { UserGroupsPage } from './pages/UserGroupsPage';
import { PlatformPermissionsPage } from './pages/PlatformPermissionsPage';
import { OrganizationStandardsPage } from './pages/OrganizationStandardsPage';
import { ExternalRecipientsPage } from './pages/ExternalRecipientsPage';
import { SuppliersPage } from './pages/SuppliersPage';
import { SupplierDetailPage } from './pages/SupplierDetailPage';
import { EquipmentPage } from './pages/EquipmentPage';
import { EquipmentDetailPage } from './pages/EquipmentDetailPage';
import { MaintenanceReportsPage } from './pages/MaintenanceReportsPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { DocumentDetailPage } from './pages/DocumentDetailPage';
import { DocumentTypesPage } from './pages/DocumentTypesPage';
import { RequirePermission } from './features/platformPermissions/components/RequirePermission';
import { QmsRecordsListPage } from './pages/QmsRecordsListPage';
import { QmsNewRecordPage } from './pages/QmsNewRecordPage';
import { QmsRecordDetailPage } from './pages/QmsRecordDetailPage';
import { QmsActionPlansPage } from './pages/QmsActionPlansPage';
import { QmsFormTemplatePage } from './pages/QmsFormTemplatePage';
import { FormTemplatePage } from './pages/FormTemplatePage';
import { QmsReportsPage } from './pages/QmsReportsPage';
import { QMS_RECLAMOS_UI_ENABLED } from './features/qms/qmsFeatures';
import { ContextPage } from './pages/ContextPage';
import { RisksPage } from './pages/RisksPage';
import { RiskDashboardPage } from './pages/RiskDashboardPage';
import { OrgUnitsPage } from './pages/OrgUnitsPage';
import { RiskStudiesPage } from './pages/RiskStudiesPage';
import { RiskStudyDetailPage } from './pages/RiskStudyDetailPage';
import { RiskCatalogPage } from './pages/RiskCatalogPage';
import { RiskDetailPage } from './pages/RiskDetailPage';
import { ObjectivesPage } from './pages/ObjectivesPage';
import { ObjectiveDetailPage } from './pages/ObjectiveDetailPage';
import { AuditsPage } from './pages/AuditsPage';
import { AuditDetailPage } from './pages/AuditDetailPage';
import { CompetencePage } from './pages/CompetencePage';
import { LegalRequirementsPage } from './pages/LegalRequirementsPage';
import { LegalRequirementDetailPage } from './pages/LegalRequirementDetailPage';
import { ManagementReviewPage } from './pages/ManagementReviewPage';
import { ManagementReviewDetailPage } from './pages/ManagementReviewDetailPage';
import { ImprovementSuggestionsPage } from './pages/ImprovementSuggestionsPage';
import { InfoAssetsPage } from './pages/InfoAssetsPage';
import { SoaPage } from './pages/SoaPage';
import { SecurityAuditPage } from './pages/SecurityAuditPage';
import { PasswordPolicyPage } from './pages/PasswordPolicyPage';
import { SafetyIncidentsPage } from './pages/SafetyIncidentsPage';
import { InfoSecurityIncidentsPage } from './pages/InfoSecurityIncidentsPage';
import { WorkerConsultationsPage } from './pages/WorkerConsultationsPage';
import { EmergencyPage } from './pages/EmergencyPage';
import { EnvironmentalAspectsPage } from './pages/EnvironmentalAspectsPage';
import { IsoImplementationProgressPage } from './pages/IsoImplementationProgressPage';
import { IsoDictionaryPage } from './pages/IsoDictionaryPage';
import { IsoImplementationRoadmapPage } from './pages/IsoImplementationRoadmapPage';
import { IsoImplementationDeliverablesPage } from './pages/IsoImplementationDeliverablesPage';
import { IsoGuideNativePage } from './pages/IsoGuideNativePage';
import { RequireStandardEnabled } from './features/organizationStandards/components/RequireStandardEnabled';
import { ChangeManagementPage } from './pages/ChangeManagementPage';
import { DesignProjectsPage } from './pages/DesignProjectsPage';
import { NonconformingOutputsPage } from './pages/NonconformingOutputsPage';
import ActionsInboxPage from './pages/ActionsInboxPage';
import SgCalendarPage from './pages/SgCalendarPage';
import { FormTaxonomiesPage } from './pages/FormTaxonomiesPage';

const WorkbookEditorPage = lazy(() => import('./pages/WorkbookEditorPage'));
const WorkbooksStoragePage = lazy(() =>
  import('./pages/WorkbooksStoragePage').then((m) => ({ default: m.WorkbooksStoragePage })),
);
const PublicWorkbookPage = lazy(() => import('./pages/PublicWorkbookPage'));
const PublicWorkbookCapturePage = lazy(() =>
  import('./pages/PublicWorkbookCapturePage').then((m) => ({ default: m.PublicWorkbookCapturePage })),
);
const FormTemplateWorkbookPage = lazy(() =>
  import('./features/formTemplates/FormTemplateWorkbookPage').then((m) => ({
    default: m.FormTemplateWorkbookPage,
  })),
);
const WorkbookCaptureTemplateEditorPage = lazy(() =>
  import('./features/formTemplates/WorkbookCaptureTemplateEditorPage').then((m) => ({
    default: m.WorkbookCaptureTemplateEditorPage,
  })),
);

function WorkbooksPageFallback() {
  return (
    <div className='flex min-h-[240px] items-center justify-center p-8 text-sm text-gray-600'>
      Cargando libros...
    </div>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme='light' storageKey='boilerplate-app-theme'>
      <ErrorBoundary>
        <AuthInitializer>
          <ToastProvider>
            <ConfirmProvider>
              <PromptProvider>
              <Routes>
                <Route path='/login' element={<LoginPage />} />
                <Route path='/forgot-password' element={<ForgotPasswordPage />} />
                <Route path='/reset-password' element={<ResetPasswordPage />} />
                <Route path='/public/surveys/:token' element={<PublicSurveyPage />} />
                <Route
                  path='/public/libros/:token'
                  element={
                    <Suspense fallback={<WorkbooksPageFallback />}>
                      <PublicWorkbookPage />
                    </Suspense>
                  }
                />
                <Route
                  path='/captura/libros/:token'
                  element={
                    <Suspense fallback={<WorkbooksPageFallback />}>
                      <PublicWorkbookCapturePage />
                    </Suspense>
                  }
                />
                <Route element={<MainLayout />}>
                  <Route element={<RequireAuth />}>
                    <Route path='/perfil' element={<PerfilPage />} />
                    <Route element={<RequirePermission permission='dashboard.access' />}>
                      <Route index element={<DashboardPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='user_groups.manage_own' />}>
                      <Route path='mis-grupos' element={<UserGroupsPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='communication_matrix.access' />}>
                      <Route path='communication-matrix' element={<CommunicationMatrixPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='suppliers.access' />}>
                      <Route path='proveedores' element={<SuppliersPage />} />
                      <Route path='proveedores/:id' element={<SupplierDetailPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='maintenance.access' />}>
                      <Route path='mantenimiento/equipos' element={<EquipmentPage />} />
                      <Route path='mantenimiento/equipos/:id' element={<EquipmentDetailPage />} />
                      <Route path='mantenimiento/reportes' element={<MaintenanceReportsPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='context.access' />}>
                      <Route path='contexto' element={<ContextPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='risks.access' />}>
                      <Route path='riesgos/tablero' element={<RiskDashboardPage />} />
                      <Route path='riesgos' element={<RisksPage />} />
                      <Route path='riesgos/:id' element={<RiskDetailPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='org_units.access' />}>
                      <Route path='niveles' element={<OrgUnitsPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='risk_studies.access' />}>
                      <Route path='estudios-riesgo' element={<RiskStudiesPage />} />
                      <Route path='estudios-riesgo/:id' element={<RiskStudyDetailPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='risk_catalog.access' />}>
                      <Route path='catalogo-riesgos' element={<RiskCatalogPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='actions_inbox.access' />}>
                      <Route path='acciones' element={<ActionsInboxPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='sg_calendar.access' />}>
                      <Route path='calendario-sg' element={<SgCalendarPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='objectives.access' />}>
                      <Route path='objetivos' element={<ObjectivesPage />} />
                      <Route path='objetivos/:id' element={<ObjectiveDetailPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='audits.access' />}>
                      <Route path='auditorias' element={<AuditsPage />} />
                      <Route path='auditorias/:id' element={<AuditDetailPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='competence.access' />}>
                      <Route path='competencias' element={<CompetencePage />} />
                    </Route>
                    <Route element={<RequirePermission permission='legal_requirements.access' />}>
                      <Route path='requisitos-legales' element={<LegalRequirementsPage />} />
                      <Route
                        path='requisitos-legales/:id'
                        element={<LegalRequirementDetailPage />}
                      />
                    </Route>
                    <Route element={<RequirePermission permission='management_review.access' />}>
                      <Route path='revision-direccion' element={<ManagementReviewPage />} />
                      <Route
                        path='revision-direccion/:id'
                        element={<ManagementReviewDetailPage />}
                      />
                    </Route>
                    <Route element={<RequirePermission permission='improvement.suggestions.access' />}>
                      <Route path='mejora/sugerencias' element={<ImprovementSuggestionsPage />} />
                    </Route>
                    <Route element={<RequireStandardEnabled standards={['iso27001']} />}>
                      <Route element={<RequirePermission permission='iso27001.assets.access' />}>
                        <Route path='iso27001/activos' element={<InfoAssetsPage />} />
                      </Route>
                      <Route element={<RequirePermission permission='iso27001.soa.access' />}>
                        <Route path='iso27001/soa' element={<SoaPage />} />
                      </Route>
                      <Route element={<RequirePermission permission='iso27001.incidents.access' />}>
                        <Route path='iso27001/incidentes' element={<InfoSecurityIncidentsPage />} />
                      </Route>
                      <Route element={<RequirePermission permission='security.audit.access' />}>
                        <Route path='iso27001/bitacora' element={<SecurityAuditPage />} />
                      </Route>
                      <Route element={<RequirePermission permission='security.password_policy.access' />}>
                        <Route path='iso27001/politica-contrasenas' element={<PasswordPolicyPage />} />
                      </Route>
                    </Route>
                    <Route element={<RequireStandardEnabled standards={['iso45001']} />}>
                      <Route element={<RequirePermission permission='iso45001.incidents.access' />}>
                        <Route path='iso45001/incidentes' element={<SafetyIncidentsPage />} />
                      </Route>
                      <Route element={<RequirePermission permission='iso45001.consultation.access' />}>
                        <Route path='iso45001/consulta' element={<WorkerConsultationsPage />} />
                      </Route>
                    </Route>
                    <Route element={<RequireStandardEnabled standards={['iso14001']} />}>
                      <Route element={<RequirePermission permission='iso14001.aspects.access' />}>
                        <Route path='iso14001/aspectos' element={<EnvironmentalAspectsPage />} />
                      </Route>
                    </Route>
                    <Route element={<RequirePermission permission='iso_implementation.access' />}>
                      <Route path='ayuda/diccionario-iso' element={<IsoDictionaryPage />} />
                      <Route element={<RequireStandardEnabled standards={['iso9001']} />}>
                        <Route
                          path='iso9001/implementacion/avance'
                          element={<IsoImplementationProgressPage standard='iso9001' />}
                        />
                        <Route
                          path='iso9001/implementacion/hoja-ruta'
                          element={<IsoImplementationRoadmapPage standard='iso9001' />}
                        />
                        <Route
                          path='iso9001/implementacion/entregables'
                          element={<IsoImplementationDeliverablesPage standard='iso9001' />}
                        />
                        <Route
                          path='iso9001/implementacion/guia'
                          element={<IsoGuideNativePage standard='iso9001' />}
                        />
                      </Route>
                      <Route element={<RequireStandardEnabled standards={['iso14001']} />}>
                        <Route
                          path='iso14001/implementacion/avance'
                          element={<IsoImplementationProgressPage standard='iso14001' />}
                        />
                        <Route
                          path='iso14001/implementacion/hoja-ruta'
                          element={<IsoImplementationRoadmapPage standard='iso14001' />}
                        />
                        <Route
                          path='iso14001/implementacion/entregables'
                          element={<IsoImplementationDeliverablesPage standard='iso14001' />}
                        />
                        <Route
                          path='iso14001/implementacion/aia'
                          element={
                            <IsoImplementationRoadmapPage standard='iso14001' guideSlug='aia' />
                          }
                        />
                      </Route>
                      <Route element={<RequireStandardEnabled standards={['iso27001']} />}>
                        <Route
                          path='iso27001/implementacion/avance'
                          element={<IsoImplementationProgressPage standard='iso27001' />}
                        />
                        <Route
                          path='iso27001/implementacion/hoja-ruta'
                          element={<IsoImplementationRoadmapPage standard='iso27001' />}
                        />
                        <Route
                          path='iso27001/implementacion/entregables'
                          element={<IsoImplementationDeliverablesPage standard='iso27001' />}
                        />
                        <Route
                          path='iso27001/implementacion/anexo-a'
                          element={<Navigate to='/iso27001/soa' replace />}
                        />
                        <Route
                          path='iso27001/implementacion/guia'
                          element={<IsoGuideNativePage standard='iso27001' />}
                        />
                      </Route>
                      <Route element={<RequireStandardEnabled standards={['iso45001']} />}>
                        <Route
                          path='iso45001/implementacion/avance'
                          element={<IsoImplementationProgressPage standard='iso45001' />}
                        />
                        <Route
                          path='iso45001/implementacion/guia'
                          element={<IsoGuideNativePage standard='iso45001' />}
                        />
                        <Route
                          path='iso45001/implementacion/hoja-ruta'
                          element={<IsoImplementationRoadmapPage standard='iso45001' />}
                        />
                        <Route
                          path='iso45001/implementacion/entregables'
                          element={<IsoImplementationDeliverablesPage standard='iso45001' />}
                        />
                      </Route>
                    </Route>
                    <Route element={<RequireStandardEnabled standards={['iso14001', 'iso45001']} />}>
                      <Route element={<RequirePermission permission='emergency.access' />}>
                        <Route path='emergencias' element={<EmergencyPage />} />
                      </Route>
                    </Route>
                    <Route element={<RequirePermission permission='change_management.access' />}>
                      <Route path='gestion-cambios' element={<ChangeManagementPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='design_development.access' />}>
                      <Route path='iso9001/diseno' element={<DesignProjectsPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='nonconforming_output.access' />}>
                      <Route path='iso9001/salidas-no-conformes' element={<NonconformingOutputsPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='documents.access' />}>
                      <Route path='documentos' element={<DocumentsPage />} />
                      <Route path='documentos/tipos' element={<DocumentTypesPage />} />
                      <Route path='documentos/:id' element={<DocumentDetailPage />} />
                    </Route>
                    <Route element={<RequireAuth allowedRoles={['superadmin', 'admin']} />}>
                      <Route
                        path='registros/no-conformidades/plantilla'
                        element={
                          <QmsFormTemplatePage
                            kind='non_conformity'
                            title='No conformidades'
                            basePath='/registros/no-conformidades'
                          />
                        }
                      />
                      {QMS_RECLAMOS_UI_ENABLED && (
                        <Route
                          path='registros/reclamos/plantilla'
                          element={
                            <QmsFormTemplatePage kind='claim' title='Reclamos' basePath='/registros/reclamos' />
                          }
                        />
                      )}
                    </Route>
                    <Route
                      path='registros/no-conformidades'
                      element={
                        <QmsRecordsListPage
                          kind='non_conformity'
                          title='No conformidades'
                          basePath='/registros/no-conformidades'
                        />
                      }
                    />
                    <Route
                      path='registros/no-conformidades/:id/planes'
                      element={
                        <QmsActionPlansPage
                          kind='non_conformity'
                          title='No conformidades'
                          basePath='/registros/no-conformidades'
                        />
                      }
                    />
                    <Route
                      path='registros/no-conformidades/:id'
                      element={
                        <QmsRecordDetailPage
                          kind='non_conformity'
                          title='No conformidades'
                          basePath='/registros/no-conformidades'
                        />
                      }
                    />
                    <Route
                      path='registros/no-conformidades/reportes'
                      element={
                        <QmsReportsPage
                          kind='non_conformity'
                          title='No conformidades'
                          basePath='/registros/no-conformidades'
                        />
                      }
                    />
                    <Route element={<RequirePermission permission='registros.non_conformities.access' />}>
                      <Route path='registros/no-conformidades/nueva' element={<QmsNewRecordPage kind='non_conformity' title='No conformidades' basePath='/registros/no-conformidades' />} />
                    </Route>
                    {QMS_RECLAMOS_UI_ENABLED && (
                      <>
                        <Route
                          path='registros/reclamos'
                          element={<QmsRecordsListPage kind='claim' title='Reclamos' basePath='/registros/reclamos' />}
                        />
                        <Route
                          path='registros/reclamos/:id/planes'
                          element={
                            <QmsActionPlansPage kind='claim' title='Reclamos' basePath='/registros/reclamos' />
                          }
                        />
                        <Route
                          path='registros/reclamos/:id'
                          element={<QmsRecordDetailPage kind='claim' title='Reclamos' basePath='/registros/reclamos' />}
                        />
                        <Route
                          path='registros/reclamos/reportes'
                          element={<QmsReportsPage kind='claim' title='Reclamos' basePath='/registros/reclamos' />}
                        />
                        <Route element={<RequirePermission permission='registros.claims.access' />}>
                          <Route
                            path='registros/reclamos/nueva'
                            element={<QmsNewRecordPage kind='claim' title='Reclamos' basePath='/registros/reclamos' />}
                          />
                        </Route>
                      </>
                    )}
                    {import.meta.env.VITE_ENABLE_SURVEYS === 'true' && (
                      <>
                        <Route element={<RequirePermission permission='assessments.surveys.access' />}>
                          <Route path='surveys' element={<SurveysPage category="survey" />} />
                          <Route path='surveys/new' element={<SurveysPage category="survey" />} />
                          <Route path='surveys/:id/responses' element={<SurveyResponsesPage />} />
                          <Route path='surveys/:id/statistics' element={<SurveyStatisticsPage />} />
                          <Route path='surveys/:id/sheet' element={<SurveyTeableSheetPage />} />
                          <Route path='surveys/:id/forms' element={<SurveyFormsPage />} />
                        </Route>
                        <Route element={<RequirePermission permission='assessments.evaluations.access' />}>
                          <Route path='evaluations' element={<SurveysPage category="evaluation" />} />
                          <Route path='evaluations/new' element={<SurveysPage category="evaluation" />} />
                          <Route path='evaluations/:id/responses' element={<SurveyResponsesPage />} />
                          <Route path='evaluations/:id/statistics' element={<SurveyStatisticsPage />} />
                          <Route path='evaluations/:id/sheet' element={<SurveyTeableSheetPage />} />
                          <Route path='evaluations/:id/forms' element={<SurveyFormsPage />} />
                        </Route>
                      </>
                    )}
                    {import.meta.env.VITE_ENABLE_SURVEYS === 'true' && (
                      <Route element={<RequirePermission permission='assessments.surveys.access' minLevel='edit' />}>
                        <Route path='/external-recipients' element={<ExternalRecipientsPage />} />
                      </Route>
                    )}
                    <Route element={<RequirePermission permission='form_templates.access' />}>
                      <Route path='f/:code' element={<FormTemplatePage />} />
                      <Route
                        path='f/:code/captura'
                        element={
                          <Suspense fallback={<WorkbooksPageFallback />}>
                            <FormTemplateWorkbookPage />
                          </Suspense>
                        }
                      />
                      <Route path='catalogo/taxonomias' element={<FormTaxonomiesPage />} />
                    </Route>
                    <Route element={<RequirePermission permission='workbooks.access' />}>
                      <Route
                        path='libros'
                        element={
                          <Suspense fallback={<WorkbooksPageFallback />}>
                            <WorkbooksStoragePage />
                          </Suspense>
                        }
                      />
                      <Route
                        path='libros/:id'
                        element={
                          <Suspense fallback={<WorkbooksPageFallback />}>
                            <WorkbookEditorPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path='libros/:id/plantilla-carga'
                        element={
                          <Suspense fallback={<WorkbooksPageFallback />}>
                            <WorkbookCaptureTemplateEditorPage />
                          </Suspense>
                        }
                      />
                    </Route>
                    {/* Rutas solo para superadmin */}
                    <Route element={<RequireAuth allowedRoles={['superadmin']} />}>
                      <Route path='/roles' element={<RolesPage />} />
                      <Route path='/users' element={<UsersPage />} />
                      <Route path='/permisos-plataforma' element={<PlatformPermissionsPage />} />
                      <Route element={<RequirePermission permission='admin.standards' minLevel='view' />}>
                        <Route path='/normas-iso' element={<OrganizationStandardsPage />} />
                        <Route path='/centro-iso/configuracion' element={<OrganizationStandardsPage />} />
                      </Route>
                    </Route>
                  </Route>
                </Route>
                <Route path='*' element={<Navigate to='/' replace />} />
              </Routes>
            </PromptProvider>
            </ConfirmProvider>
          </ToastProvider>
        </AuthInitializer>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
